import { AxiosError, isAxiosError } from 'axios'

import type { ApiResponse, NormalizedApiError, ProblemDetails } from '@/types/api'

const GENERIC_MESSAGE = 'Não foi possível concluir a operação. Tente novamente.'

/** Extrai `data` do envelope padrão da API. */
export function unwrap<T>(response: { data: ApiResponse<T> }): T {
  return response.data.data
}

function defaultTitle(status: number): string {
  switch (status) {
    case 400:
      return 'Requisição inválida'
    case 401:
      return 'Sessão expirada'
    case 403:
      return 'Acesso negado'
    case 404:
      return 'Não encontrado'
    case 409:
      return 'Conflito'
    case 422:
      return 'Dados inválidos'
    case 500:
      return 'Erro no servidor'
    default:
      return 'Erro'
  }
}

/** Código estável do erro da API (`errorCode`, ou `code` em algumas rotas). */
export function codigoDoErro(error: unknown): string | undefined {
  if (!isAxiosError(error)) return undefined
  const problem = (error.response?.data ?? {}) as ProblemDetails
  return problem.errorCode ?? problem.code
}

/** Converte qualquer erro (axios, rede, genérico) em uma forma estável para a UI. */
export function normalizeApiError(error: unknown): NormalizedApiError {
  if (error instanceof AxiosError) {
    if (!error.response) {
      const timedOut = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT'
      return {
        status: 0,
        title: 'Falha de conexão',
        detail: timedOut
          ? 'A requisição demorou demais. Verifique sua conexão e tente novamente.'
          : 'Não foi possível falar com o servidor. Verifique sua conexão.',
        fieldErrors: {},
        isNetworkError: true,
      }
    }

    const status = error.response.status
    const problem = (error.response.data ?? {}) as ProblemDetails

    const fieldErrors: Record<string, string> = {}
    if (problem.errors) {
      for (const [key, messages] of Object.entries(problem.errors)) {
        // "NomeUsuario" -> "nomeUsuario", casando com os names do react-hook-form.
        const field = key.charAt(0).toLowerCase() + key.slice(1)
        // A API pode mandar várias mensagens por campo; todas já vêm prontas para o usuário.
        const text = (messages ?? []).filter(Boolean).join(' ')
        fieldErrors[field] = text || GENERIC_MESSAGE
      }
    }

    if (status === 429) {
      return {
        status,
        title: 'Muitas tentativas',
        detail:
          'Você fez muitas requisições em pouco tempo. Aguarde um instante e tente novamente.',
        fieldErrors,
        isNetworkError: false,
        errorCode: problem.errorCode ?? problem.code,
      }
    }

    const hasFieldErrors = Object.keys(fieldErrors).length > 0
    return {
      status,
      title: problem.title || defaultTitle(status),
      detail:
        problem.detail ||
        (hasFieldErrors ? 'Verifique os campos destacados.' : defaultTitle(status)),
      fieldErrors,
      isNetworkError: false,
      errorCode: problem.errorCode ?? problem.code,
    }
  }

  return {
    status: -1,
    title: 'Erro inesperado',
    detail: error instanceof Error ? error.message : GENERIC_MESSAGE,
    fieldErrors: {},
    isNetworkError: false,
  }
}
